import React from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';

function Dashboard() {
  const navigate = useNavigate();

  const dashboards = [
    {
      id: 'jobs',
      title: 'Jobs Analytics',
      icon: '🔧',
      description: 'Job performance, completion rates, time tracking',
      color: 'from-blue-500 to-blue-600',
      path: '/analytics/jobs'
    },
    {
      id: 'quotes',
      title: 'Quotes Analytics',
      icon: '📝',
      description: 'Pipeline, conversion rates, win/loss analysis',
      color: 'from-green-500 to-green-600',
      path: '/analytics/quotes'
    },
    {
      id: 'invoices',
      title: 'Invoices Analytics',
      icon: '💰',
      description: 'Payment status, outstanding amounts, cash flow',
      color: 'from-orange-500 to-orange-600',
      path: '/analytics/invoices'
    },
    {
      id: 'financial',
      title: 'Financial Dashboard',
      icon: '📊',
      description: 'Revenue, profit, expenses, financial health',
      color: 'from-purple-500 to-purple-600',
      path: '/analytics/financial'
    },
    {
      id: 'ppm',
      title: 'PPM Analytics',
      icon: '🔄',
      description: 'Contract performance, recurring revenue, maintenance',
      color: 'from-pink-500 to-pink-600',
      path: '/analytics/ppm'
    },
    {
      id: 'customers',
      title: 'Customer Analytics',
      icon: '👥',
      description: 'Customer activity, growth trends, top accounts',
      color: 'from-indigo-500 to-indigo-600',
      path: '/analytics/customers'
    },
    {
      id: 'engineers',
      title: 'Engineers Analytics',
      icon: '👷',
      description: 'Performance, revenue per engineer, utilization, productivity',
      color: 'from-blue-600 to-blue-700',
      path: '/analytics/engineers'
    },
    {
      id: 'schedule',
      title: 'Schedule Analytics',
      icon: '📅',
      description: 'Resource utilization, appointments, time analysis',
      color: 'from-teal-500 to-teal-600',
      path: '/analytics/schedule'
    },
    {
      id: 'fleet',
      title: 'Fleet Analytics',
      icon: '🚗',
      description: 'Vehicle usage, costs, maintenance tracking',
      color: 'from-cyan-500 to-cyan-600',
      path: '/analytics/fleet'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader 
        title="Analytics Dashboard"
        subtitle="Business Intelligence & Performance Metrics"
        backButton
        onBack={() => navigate('/')}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        
        {/* Welcome Section */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Analytics</h2>
          <p className="text-gray-600">
            Select an analytics dashboard below to view detailed metrics, trends, and performance indicators.
            Each dashboard provides time-based comparisons, visual charts, and actionable insights.
          </p>
        </div>

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {dashboards.map(dashboard => (
            <div
              key={dashboard.id}
              onClick={() => navigate(dashboard.path)}
              className={`bg-gradient-to-br ${dashboard.color} rounded-xl shadow-lg p-6 text-white cursor-pointer transform transition-all hover:scale-105 hover:shadow-2xl`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                  <span className="text-4xl">{dashboard.icon}</span>
                </div>
              </div>
              <h3 className="text-xl font-bold mb-2">{dashboard.title}</h3>
              <p className="text-white/90 text-sm leading-relaxed">
                {dashboard.description}
              </p>
              <div className="mt-4 flex items-center text-sm font-semibold">
                <span>View Dashboard</span>
                <span className="ml-2">→</span>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Stats Section */}
        <div className="mt-8 bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Overview</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
              <div className="text-sm text-gray-600 mb-1">Active Jobs</div>
              <div className="text-2xl font-bold text-gray-900">-</div>
              <div className="text-xs text-gray-500 mt-1">Click Jobs Analytics</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
              <div className="text-sm text-gray-600 mb-1">Pending Quotes</div>
              <div className="text-2xl font-bold text-gray-900">-</div>
              <div className="text-xs text-gray-500 mt-1">Click Quotes Analytics</div>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg border-l-4 border-orange-500">
              <div className="text-sm text-gray-600 mb-1">Outstanding Invoices</div>
              <div className="text-2xl font-bold text-gray-900">-</div>
              <div className="text-xs text-gray-500 mt-1">Click Invoices Analytics</div>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border-l-4 border-purple-500">
              <div className="text-sm text-gray-600 mb-1">Monthly Revenue</div>
              <div className="text-2xl font-bold text-gray-900">-</div>
              <div className="text-xs text-gray-500 mt-1">Click Financial Dashboard</div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-8 bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6">
          <div className="flex items-start">
            <div className="text-3xl mr-4">ℹ️</div>
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">About Analytics Dashboards</h4>
              <p className="text-gray-700 mb-2">
                Each specialized dashboard provides:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li><strong>Time-based filtering</strong> - View data by month, quarter, year, or custom date ranges</li>
                <li><strong>Comparison metrics</strong> - Current period vs previous period with trend indicators</li>
                <li><strong>Visual charts</strong> - Pie charts, bar graphs, and trend lines</li>
                <li><strong>KPI tracking</strong> - Key performance indicators at a glance</li>
                <li><strong>Export options</strong> - Download reports and data (coming soon)</li>
              </ul>
            </div>
          </div>
        </div>

      </main>
    </div>
  );
}

export default Dashboard;
